import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Carregando, Erro } from '../../componentes/estados';
import { CampoData, Rotulo, EntradaSensivel } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { ErroRequisicao, del, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, buscarTudo, useAgendaAoVivo } from './dados';
import { EventoDoGoogle } from './EventoDoGoogle';
import type { Bloqueio } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';

/** Bloqueios de um recurso daqui para frente, com formulário para adicionar um período e remover os manuais. */
export function Bloqueios({ recursoId }: { recursoId: string }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: [CHAVE, 'bloqueios-do-recurso', recursoId], queryFn: () => buscarTudo<Bloqueio>('/agenda/bloqueios', { recurso_id: recursoId, de: new Date().toISOString() }) });
  useAgendaAoVivo();
  const [eventoGoogle, setEventoGoogle] = useState<string | null>(null);
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  useValidacaoLocal(null, erro);
  const [forcar, setForcar] = useState(false);

  const recarregar = () => qc.invalidateQueries({ queryKey: [CHAVE] });
  const criar = useMutation({
    mutationFn: () => post('/agenda/bloqueios', { recurso_id: recursoId, inicio: new Date(inicio).toISOString(), fim: new Date(fim).toISOString(), motivo: motivo.trim() || null, forcar }),
    onMutate: () => setErro(null),
    onSuccess: async () => {
      setInicio('');
      setFim('');
      setMotivo('');
      setForcar(false);
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => {
      setErro(mensagemDeErro(e));
      if (e instanceof ErroRequisicao && e.status === 409) setForcar(true);
    },
  });
  const remover = useMutation({
    mutationFn: (id: string) => del(`/agenda/bloqueios/${id}`),
    onSuccess: async () => {
      await recarregar();
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Val5 (29/10): bloquear horário tem ação própria (`agenda.bloquear`).
  const pode = s.pode('agenda.bloquear');

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold">{t('bloqueios.titulo')}</h3>
        <p className="text-xs text-tinta-3">{t('bloqueios.ajuda')}</p>
      </div>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : q.data.length === 0 ? (
        <p className="text-sm text-tinta-3">{t('bloqueios.vazio')}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {q.data.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-borda px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="numero">
                  {dataHora(b.inicio)} a {dataHora(b.fim)}
                </span>
                {(b.titulo_google || b.motivo) && <span className="ml-2 text-tinta-3">{textoLivre(b.titulo_google || b.motivo)}</span>}
              </span>
              <span className="flex items-center gap-2">
                <Selo cor={b.origem === 'google' ? 'ciano' : 'neutro'}>{b.origem === 'google' ? t('bloqueios.origemGoogle') : t('bloqueios.origemManual')}</Selo>
                {b.meu_google && s.pode('agenda.criar') && <Botao variante="fantasma" tamanho="sm" aria-label={t('eventoGoogle.abrir')} onClick={() => setEventoGoogle(b.id)} icone={<Pencil className="size-4" />} data-abrir-evento-google="" />}
                {pode && b.origem === 'manual' && <Botao variante="fantasma" tamanho="sm" aria-label={t('acoes.remover')} onClick={() => remover.mutate(b.id)} icone={<Trash2 className="size-4" />} />}
              </span>
            </li>
          ))}
        </ul>
      )}
      {pode && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-borda bg-sup-2 p-3 sm:grid-cols-2">
          {erro && (
            <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-2">
              {erro}
            </p>
          )}
          <Rotulo id="bl-ini" rotulo={t('campos.inicio')}>
            <CampoData id="bl-ini" type="datetime-local" value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </Rotulo>
          <Rotulo id="bl-fim" rotulo={t('campos.fim')}>
            <CampoData id="bl-fim" type="datetime-local" value={fim} onChange={(e) => setFim(e.target.value)} />
          </Rotulo>
          <div className="sm:col-span-2">
            <Rotulo id="bl-mot" rotulo={t('campos.motivo')}>
              <EntradaSensivel mascara={null} livre id="bl-mot" maxLength={200} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </Rotulo>
          </div>
          <div className="sm:col-span-2">
            <Botao disabled={!inicio || !fim} carregando={criar.isPending} onClick={() => criar.mutate()}>
              {forcar ? t('acoes.forcarBloqueio') : t('acoes.adicionarBloqueio')}
            </Botao>
          </div>
        </div>
      )}
      {eventoGoogle && <EventoDoGoogle bloqueioId={eventoGoogle} aoFechar={() => setEventoGoogle(null)} />}
    </section>
  );
}
